import { Flex, Text } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { getOutOfSpecFrames } from 'classes/helpers/vision-install.helper';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTabs } from 'components/common/tabs';
import { VisionCalibrationBoardSelector } from 'components/machine/dialogs/vision-calibration/board-selector';
import { VisionCalibrationDataCollectionTab } from 'components/machine/dialogs/vision-calibration/camera/data-collection.tab';
import { VisionCalibrationCameraResultTab } from 'components/machine/dialogs/vision-calibration/camera/results.tab';
import { MachineContext } from 'contexts/machine.context';
import useLatest from 'hooks/useLatestRef';
import { IVerboseComponent } from 'interfaces/i-components';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  VisionCameraBoard,
  VisionCalibrationCameraType,
} from 'lib_ts/enums/vision.enums';
import {
  IVisionCalibrationStatus,
  IVisionCameraProgress,
  IVisionCameraRequest,
} from 'lib_ts/interfaces/machine-msg/vision/i-calibrate-camera';
import {
  IVisionCameraCalibration,
  IOutOfSpecFrame,
} from 'lib_ts/interfaces/vision/i-vision-camera-calibration';
import { cloneDeep, max } from 'lodash';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  VisionCameraCalibrationsService as VCCService,
  VisionCameraCalibrationsService,
} from 'services/vision-camera-calibrations';
import { WebSocketService } from 'services/web-socket.service';

export interface IVccProps extends IVerboseComponent {
  vccID: string;
  onComplete: (model: IVisionCameraCalibration) => void;
  onVccChange?: (model: IVisionCameraCalibration) => void;
}

export const ALLOW_RETAKING_CAL_IMAGES = false;

enum TabKey {
  BoardSelection = 'BoardSelection',
  DataCollection = 'DataCollection',
  Results = 'Results',
}

const getNextWorkingIdx = (
  frameIndices: number[],
  totalFrames: number
): number => {
  const maxKnownIdx = max(frameIndices) ?? 0;

  // try to find the index of the first missing frame to resume from (needed in case frames were skipped during calibration)
  for (let i = 0; i <= maxKnownIdx; i++) {
    const isFound = frameIndices.findIndex((idx) => idx === i) !== -1;

    // return the first index without a captured frame
    if (!isFound) {
      return i;
    }
  }

  // everything up to the maxKnownIdx was present, so return the next index (without exceeding the expected total)
  return Math.min(maxKnownIdx + 1, totalFrames - 1);
};

export type { IOutOfSpecFrame } from 'lib_ts/interfaces/vision/i-vision-camera-calibration';

export const VisionCalibrationCamera: FC<IVccProps> = (props) => {
  const { setSpecialMode } = useContext(MachineContext);
  const { onVccChange } = props;

  const applyVcc = useCallback(
    (next: IVisionCameraCalibration) => {
      setVcc(next);
      onVccChange?.(next);
    },
    [onVccChange]
  );

  // todo: clarify why we need this in a comment
  useEffect(() => {
    setSpecialMode('stereo-check');
    return () => setSpecialMode(undefined);
  }, []);

  const [activeTab, setActiveTab] = useState(TabKey.BoardSelection);
  const [vcc, setVcc] = useState<IVisionCameraCalibration>();

  // state for retaking images
  const [isRetaking, setIsRetaking] = useState(false);
  const [outOfSpecFrames, setOutOfSpecFrames] = useState<IOutOfSpecFrame[]>([]); // frames above cutoff
  const [retakenFrameIndices, setRetakenFrameIndices] = useState<number[]>([]);
  const [frameIndicesToRetake, setFrameIndicesToRetake] = useState<number[]>( // currently limited to one retake at a time
    []
  );

  // change tabs automatically
  useEffect(() => {
    if (!vcc?.board) {
      // force setup step if no board is selected
      setActiveTab(TabKey.BoardSelection);
      return;
    }

    if (vcc.complete) {
      // go to results tab if reviewing a (previously) completed calibration
      setActiveTab(TabKey.Results);
      return;
    }

    setActiveTab(TabKey.DataCollection);
  }, [vcc?.board, vcc?.complete]);

  // which image we want to work on
  const [workingIdx, setWorkingIdx] = useState(0);
  const [lastProgress, setLastProgress] = useState<IVisionCameraProgress>();

  // load vcc and determine where to start
  useEffect(() => {
    const callback = async () => {
      const response = await VCCService.getInstance().getOne(props.vccID);

      if (response) {
        applyVcc(response);
      }

      if (!response?.result) {
        return;
      }
    };

    callback();
  }, [props.vccID, applyVcc]);

  // whenever captured frames change, try to go to the first missing index
  useEffect(() => {
    if (!vcc?.capturedFrames) {
      // wait until vcc loads
      return;
    }

    // TS doesn't inferr properly if using isNil here
    if (
      lastProgress?.totalFrames === undefined ||
      lastProgress?.totalFrames === null
    ) {
      // wait until first progress arrives
      return;
    }

    if (isRetaking) {
      return;
    }

    const nextWorkingIdx = getNextWorkingIdx(
      vcc.capturedFrames.map((f) => f.index),
      lastProgress.totalFrames
    );

    verboseDebug(props, `Setting working frame > ${nextWorkingIdx}`);
    setWorkingIdx(nextWorkingIdx);
  }, [
    lastProgress?.totalFrames,
    vcc?.capturedFrames,
    isRetaking,
    frameIndicesToRetake,
    retakenFrameIndices,
  ]);

  const latestVcc = useLatest(vcc);

  // send machine instructions whenever board and/or working index changes
  useEffect(() => {
    // timeout prevents spamming the machine if multiple things change quickly (e.g. after restarting)
    const timeout = setTimeout(() => {
      if (!vcc?.board) {
        return;
      }

      if (activeTab !== TabKey.DataCollection) {
        return;
      }

      const capturedFrames = cloneDeep(latestVcc.current?.capturedFrames) || [];
      const workingFrame = capturedFrames[workingIdx];

      // for FW to know this frame needs to be retaken
      if (isRetaking && workingFrame) {
        capturedFrames.splice(workingIdx, 1, {
          ...workingFrame,
          success: false,
        });
      }

      const data: IVisionCameraRequest = {
        board_type: vcc.board,
        goToFrameIdx: workingIdx,
        capturedFrames,
      };

      verboseDebug(props, 'Sending request', data);
      WebSocketService.send(WsMsgType.U2M_VisionCameraRequest, data, 'VCC');
    }, 500);

    return () => clearTimeout(timeout);
  }, [workingIdx, vcc?.board, activeTab, isRetaking]);

  // listen for progress messages
  useEffect(() => {
    if (activeTab !== TabKey.DataCollection) {
      // only listen while on DC tab
      return;
    }

    if (!vcc) {
      // only listen once we have a VCC to store the results in
      return;
    }

    const callback = async (event: CustomEvent) => {
      const data: IVisionCameraProgress = event.detail;
      verboseDebug(props, 'Received progress', data);
      setLastProgress(data);

      const payload: Partial<IVisionCameraCalibration> = {};

      const { capturedFrame, result: calibrationResult } = data;

      // handle if frame is provided
      if (capturedFrame && capturedFrame.success) {
        const nextFrames = cloneDeep(vcc.capturedFrames ?? []).filter(
          // exclude the existing frame (if any) for the same index
          (f) => f.index !== capturedFrame.index
        );
        // append the new frame to the list
        nextFrames.push(capturedFrame);

        // ensure frames are in sequential order
        nextFrames.sort((a, b) => (a.index < b.index ? -1 : 1));

        payload.capturedFrames = nextFrames;
      }

      // handle if result is provided
      if (calibrationResult) {
        payload.result = calibrationResult;
      }

      if (Object.keys(payload).length === 0) {
        return;
      }

      const savedVcc = await VCCService.getInstance().updateOne(
        vcc._id,
        payload
      );

      if (!savedVcc) {
        console.warn(
          `Failed to update VCC ${vcc._id} with updated captured frames and/or results`
        );
        return;
      }

      applyVcc(savedVcc);

      if (!isRetaking) {
        return;
      }

      if (!capturedFrame || !capturedFrame.success) {
        return;
      }

      // which images have been retaken already
      setRetakenFrameIndices((prev) =>
        ArrayHelper.unique([...prev, capturedFrame.index]).sort((a, b) => a - b)
      );
    };

    WebSocketHelper.on(WsMsgType.M2U_VisionCameraProgress, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_VisionCameraProgress, callback);
    };
  }, [vcc, activeTab, isRetaking]);

  // detect final result => change tabs (during first data collection)
  useEffect(() => {
    if (activeTab === TabKey.Results) {
      return;
    }

    if (!vcc?.result) {
      return;
    }

    // if we're already retaking images we'll always have results, so don't prematurely switch tabs
    if (isRetaking) {
      return;
    }

    const outOfSpec = getOutOfSpecFrames(vcc.result);
    setOutOfSpecFrames(outOfSpec);
    setActiveTab(TabKey.Results);
  }, [vcc?.result, isRetaking]);

  // done retaking all bad images => change tabs (only when retaking)
  useEffect(() => {
    if (!isRetaking) {
      return;
    }

    if (retakenFrameIndices.length < frameIndicesToRetake.length) {
      return;
    }

    if (!vcc?.result) {
      return;
    }

    // recalculate out of spec frames post-retake and reset retake state
    const outOfSpec = getOutOfSpecFrames(vcc.result);
    setOutOfSpecFrames(outOfSpec);
    setActiveTab(TabKey.Results);
    setIsRetaking(false);
    setFrameIndicesToRetake([]);
  }, [vcc?.result, retakenFrameIndices, isRetaking, frameIndicesToRetake]);

  const status = useMemo<IVisionCalibrationStatus>(() => {
    const output: IVisionCalibrationStatus = {
      capturing: false,
      label: 'Place the board in the blue square',
      maxIdx: 0,
      totalFrames: 1,
    };

    if (vcc?.capturedFrames) {
      const maxIndex = max(vcc.capturedFrames.map((f) => f.index));

      output.maxIdx = maxIndex ?? 0;
      output.totalFrames = maxIndex ?? 1;
    }

    if (lastProgress?.autoCapturing) {
      output.capturing = true;
    }

    if (lastProgress?.totalFrames) {
      output.totalFrames = lastProgress.totalFrames;
    }

    return output;
  }, [lastProgress, vcc]);

  const [confirmReset, setConfirmReset] = useState<number>();

  const onRetakeImages = useCallback(
    async (startingIndex: number) => {
      if (!vcc?._id) {
        return;
      }

      setRetakenFrameIndices([]);
      setIsRetaking(true);
      setWorkingIdx(startingIndex);
      // just one for now
      setFrameIndicesToRetake([startingIndex]);
      setActiveTab(TabKey.DataCollection);
    },
    [vcc?._id]
  );

  const onComplete = async () => {
    if (!vcc) {
      return;
    }

    if (!vcc.result) {
      // shouldn't trigger
      return;
    }

    if (!vcc.board) {
      // shouldn't trigger
      return;
    }

    const data: IVisionCameraRequest = {
      board_type: vcc.board,
      saveResult: vcc.result,
      capturedFrames: vcc.capturedFrames ?? [],
    };

    await WebSocketService.send(WsMsgType.U2M_VisionCameraRequest, data, 'VCC');

    const savedVcc = await VCCService.getInstance().updateOne(vcc._id, {
      complete: true,
    });

    if (!savedVcc) {
      console.warn(`Failed to update VCC ${vcc._id} with complete flag`);
      return;
    }

    applyVcc(savedVcc);

    verboseDebug(props, 'Complete', savedVcc);

    NotifyHelper.success({
      message_md:
        'Stereo Camera Calibration complete. \n\n Please proceed to Distance to Plate Calibration to confirm.',
    });
    props.onComplete(savedVcc);
  };

  const onSendToSlack = async (
    charts: { cameraType: VisionCalibrationCameraType; imageBase64: string }[]
  ) => {
    if (!vcc?.result) {
      return;
    }

    if (!vcc?.board) {
      return;
    }

    const totalLeaderFrames = vcc.result.leader.reprojection.length;
    const numLeaderPassing = vcc.result.leader.reprojection.filter(
      (v) => vcc.result && v < vcc.result.leader.cutoff
    ).length;

    const totalFollowerFrames = vcc.result.follower.reprojection.length;
    const numFollowerPassing = vcc.result.follower.reprojection.filter(
      (v) => vcc.result && v < vcc.result.follower.cutoff
    ).length;

    const totalStereoFrames = vcc.result.stereo.reprojection.length;
    const numStereoPassing = vcc.result.stereo.reprojection.filter(
      (v) => vcc.result && v < vcc.result.stereo.cutoff
    ).length;

    const textRows = [
      `*${vcc.machineID} Stereo Calibration Results (ID: ${vcc._id})*`,
      `\n- *Board Type*: ${vcc.board}`,
      `\n- *Leader Results*: ${numLeaderPassing}/${totalLeaderFrames} ${numLeaderPassing === totalLeaderFrames ? ':white_check_mark:' : ':x:'}`,
      `\n- *Follower Results*: ${numFollowerPassing}/${totalFollowerFrames} ${numFollowerPassing === totalFollowerFrames ? ':white_check_mark:' : ':x:'}`,
      `\n- *Stereo Results*: ${numStereoPassing}/${totalStereoFrames} ${numStereoPassing === totalStereoFrames ? ':white_check_mark:' : ':x:'}`,
    ];

    await VisionCameraCalibrationsService.getInstance().uploadToSlack({
      results: charts.map(({ cameraType, imageBase64 }) => ({
        cameraType,
        filename: `${vcc.machineID}-${cameraType}-${vcc._id}.png`,
        imageBase64,
        title: `${vcc.machineID} ${cameraType} results`,
      })),
      board: vcc.board,
      vccId: vcc._id,
      machineID: vcc.machineID,
      text_md: textRows.join(''),
    });
  };

  return (
    <ErrorBoundary identifier="VisionCalibrationCamera">
      <Flex direction="column" gap="2">
        <Text color={RADIX.COLOR.SECONDARY}>
          This step aligns the two internal cameras (Leader and Follower) to
          ensure accurate depth perception and tracking. The system will guide
          you through capturing a series of images using a calibration
          chessboard.
        </Text>
        <CommonTabs
          gap="0"
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as TabKey)}
          tabs={[
            {
              value: TabKey.BoardSelection,
              label: 'Board Selection',
              title: vcc?.board
                ? 'Reset progress to change the board type'
                : undefined,
              disabled: !!vcc?.board,
              content: (
                <Flex direction="column" gap="4">
                  <Text color={RADIX.COLOR.SECONDARY}>
                    Please select the board type you are going to use for the
                    calibration.
                  </Text>

                  <VisionCalibrationBoardSelector
                    value={vcc?.board ?? null}
                    onChange={async (value: VisionCameraBoard) => {
                      verboseDebug(props, 'Board selected', value);

                      if (!vcc) {
                        NotifyHelper.warning({
                          message_md:
                            'Please wait for calibration data to load and try again.',
                        });
                        return;
                      }

                      const savedVcc = await VCCService.getInstance().updateOne(
                        vcc._id,
                        { board: value }
                      );

                      if (!savedVcc) {
                        console.warn(
                          `Failed to update VCC ${vcc._id} with updated board type`
                        );
                        return;
                      }

                      applyVcc(savedVcc);
                    }}
                  />
                </Flex>
              ),
            },
            {
              value: TabKey.DataCollection,
              label: 'Data Collection',
              // make sure the user has actually elected to retake images
              disabled: !vcc?.board,
              content: (
                <VisionCalibrationDataCollectionTab
                  status={status}
                  capturedFrames={vcc?.capturedFrames || []}
                  workingIdx={workingIdx}
                  loading={!lastProgress}
                  frameIndicesToRetake={isRetaking ? frameIndicesToRetake : []}
                  setWorkingIdx={setWorkingIdx}
                  onReset={setConfirmReset}
                />
              ),
            },
            {
              value: TabKey.Results,
              label: 'Results',
              disabled: !vcc?.board,
              invisible: !vcc?.result,
              content: vcc?.result && (
                <Flex direction="column" gap="4" align="center">
                  <VisionCalibrationCameraResultTab
                    result={vcc.result}
                    outOfSpecFrames={outOfSpecFrames}
                    onRetakeImages={onRetakeImages}
                    onComplete={onComplete}
                    onReportResults={onSendToSlack}
                  />
                </Flex>
              ),
            },
          ]}
        />
      </Flex>

      {confirmReset && (
        <CommonConfirmationDialog
          key={confirmReset}
          identifier="VisionCameraStartOver"
          title="Start Over"
          content={
            <p>
              You are about to reset your board type and all captured frames,
              and then start over. Are you sure?
            </p>
          }
          action={{
            label: 'Start Over',
            onClick: async () => {
              if (!vcc) {
                return;
              }

              const savedVcc = await VCCService.getInstance().updateOne(
                vcc._id,
                {
                  // wiping the board should set the UI back to the board selection tab
                  board: null,
                  capturedFrames: [],
                  result: null,
                  complete: false,
                }
              );

              if (!savedVcc) {
                verboseDebug(props, 'Start over > failed to save', savedVcc);
                console.warn(
                  `Failed to update VCC ${vcc._id} with emptied captured frames and/or results`
                );
                return;
              }

              verboseDebug(props, 'Start over', savedVcc);

              applyVcc(savedVcc);
              setWorkingIdx(0);
            },
          }}
        />
      )}
    </ErrorBoundary>
  );
};
